import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { CheckCircle, AlertTriangle, ExternalLink, Loader2 } from "lucide-react"

type Eintrag = {
  art: string
  id: string
  gefunden: boolean
  titel: string
  zeilen: string[]
  warnung: string
  hero_url: string
}

/**
 * Gegenprobe: was steht wirklich in HERO?
 *
 * Der Verlauf darueber sagt, was das Portal getan zu haben glaubt ("Projekt
 * angelegt"). Das ist die Sicht des Portals — und die kann von HERO abweichen:
 * ein Projekt an einem geloeschten Kontakt, eine Projektadresse, die still auf
 * die des Kontakts zurueckgefallen ist, ein Termin, den jemand in HERO wieder
 * geloescht hat. Diese Ansicht holt die Objekte frisch und sagt, was auffaellt.
 *
 * Bewusst auf Klick: jeder Aufruf sind drei HERO-Abfragen, und die will man
 * nicht bei jedem Oeffnen einer Mail.
 */
export function HeroAbgleich({ emailId }: { emailId: number }) {
  const [aktiv, setAktiv] = useState(false)
  const q = useQuery<{ geprueft_am: string; eintraege: Eintrag[]; hinweis: string }>({
    queryKey: ["hero-abgleich", emailId],
    queryFn: async () => {
      const r = await fetch(`/api/v1/emails/${emailId}/hero-abgleich`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    enabled: aktiv,
    staleTime: 0,
  })

  if (!aktiv) {
    return (
      <button type="button" className="ev2-pk-linkbtn" style={{ fontSize: 12, marginTop: 8 }}
        onClick={() => setAktiv(true)}
        title="Fragt HERO direkt, was unter den verknüpften IDs steht">
        In HERO prüfen
      </button>
    )
  }

  return (
    <div style={{ marginTop: 8 }}>
      {q.isFetching && (
        <div style={{ fontSize: 12, color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 6 }}>
          <Loader2 className="w-3 h-3 animate-spin" /> Frage HERO…
        </div>
      )}
      {q.isError && (
        <div style={{ fontSize: 12, color: "var(--danger-color)" }}>
          Abgleich fehlgeschlagen: {q.error instanceof Error ? q.error.message : "unbekannt"}
        </div>
      )}
      {q.data && (
        <>
          {q.data.hinweis && (
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{q.data.hinweis}</div>
          )}
          {q.data.eintraege.map((e) => (
            <div key={e.art + e.id} style={{ marginBottom: 10, fontSize: 12.5 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {e.gefunden && !e.warnung
                  ? <CheckCircle className="w-3.5 h-3.5" style={{ color: "#059669", flexShrink: 0 }} />
                  : <AlertTriangle className="w-3.5 h-3.5" style={{ color: "#9a3412", flexShrink: 0 }} />}
                <strong style={{ minWidth: 0 }}>{e.titel || `${e.art} #${e.id}`}</strong>
                {e.hero_url && (
                  <a href={e.hero_url} target="_blank" rel="noreferrer"
                    style={{ marginLeft: "auto", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 3 }}>
                    HERO <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              {e.zeilen.map((z, i) => (
                <div key={i} style={{ color: "var(--text-secondary)", paddingLeft: 20 }}>{z}</div>
              ))}
              {e.warnung && (
                <div className="ev2-pk-warnung" style={{ paddingLeft: 20 }}>{e.warnung}</div>
              )}
            </div>
          ))}
          <div style={{ fontSize: 12, color: "var(--text-muted)", display: "flex", gap: 8 }}>
            <span>Stand {q.data.geprueft_am}</span>
            <button type="button" className="ev2-pk-linkbtn" style={{ fontSize: 12 }}
              onClick={() => q.refetch()}>erneut prüfen</button>
          </div>
        </>
      )}
    </div>
  )
}
